import * as d3 from 'd3'
import { useMemo } from 'react'
import styles from './HeatmapGrid.module.css'

export interface HeatmapGridProps {
  /** [7][24] — matrix[dayOfWeek][hour]. dayOfWeek 0=일 ~ 6=토, hour 0~23. */
  matrix: number[][]
  /** 셀 hover 시 title 문구. (dayLabel, hour, value) → text */
  formatCell?: (dayLabel: string, hour: number, value: number) => string
  /** 요일 라벨(0=일 순). 기본 ['일','월','화','수','목','금','토']. */
  dayLabels?: string[]
  /** 셀 색상 베이스. 기본 파랑 계열. */
  color?: string
  /** 셀 클릭 콜백 — (dayOfWeek, hour). 지정 시 셀이 클릭 가능해진다. */
  onCellClick?: (dayOfWeek: number, hour: number) => void
}

const DEFAULT_DAYS = ['일', '월', '화', '수', '목', '금', '토']
const HOURS = Array.from({ length: 24 }, (_, h) => h)

/**
 * 요일 × 시간(7×24) 히트맵. admin ui/charts 에 히트맵 프리미티브가 없어 페이지 로컬로 구현.
 * 각 셀 배경을 값/최댓값 비율로 보간(0 셀은 옅은 뉴트럴). CSS grid 로 레이아웃.
 */
export function HeatmapGrid({
  matrix,
  formatCell,
  dayLabels = DEFAULT_DAYS,
  color = '#7c3aed',
  onCellClick,
}: HeatmapGridProps) {
  const max = useMemo(() => {
    let m = 0
    for (const row of matrix) for (const v of row) if (v > m) m = v
    return m
  }, [matrix])

  // 0 → 흰색, max → color 로 보간하는 순차 스케일
  const scale = useMemo(() => {
    const interp = d3.interpolate('#F5F7FA', color)
    return (v: number) => (max > 0 ? interp(v / max) : '#F5F7FA')
  }, [max, color])

  return (
    <div className={styles.wrap}>
      <div className={styles.grid} style={{ gridTemplateColumns: `2.5rem repeat(24, 1fr)` }}>
        {/* 헤더: 시간 라벨 (짝수 시만 표기) */}
        <div className={styles.corner} />
        {HOURS.map((h) => (
          <div key={`h-${h}`} className={styles.hourLabel}>
            {h % 3 === 0 ? h : ''}
          </div>
        ))}

        {/* 7행: 요일 */}
        {dayLabels.map((day, dow) => (
          <div key={`row-${dow}`} className={styles.row} style={{ display: 'contents' }}>
            <div className={styles.dayLabel}>{day}</div>
            {HOURS.map((h) => {
              const value = matrix[dow]?.[h] ?? 0
              const clickable = onCellClick != null
              const baseTitle = formatCell ? formatCell(day, h, value) : `${day} ${h}시: ${value}`
              return (
                <div
                  key={`c-${dow}-${h}`}
                  className={`${styles.cell} ${clickable ? styles.cellClickable : ''}`}
                  style={{ backgroundColor: scale(value) }}
                  title={clickable ? `${baseTitle} (클릭해 상세 보기)` : baseTitle}
                  onClick={clickable ? () => onCellClick(dow, h) : undefined}
                />
              )
            })}
          </div>
        ))}
      </div>
    </div>
  )
}
